/**
 * §P.8 fase 2 · Las solicitudes de derechos RGPD que recibe el club.
 *
 * ## El plazo es legal: un mes
 *
 * «Pasarse no es una métrica interna: es una sanción.» Así que lo primero que dice la
 * pantalla es cuántas están fuera de plazo, y una vencida sale en rojo y arriba. Una
 * bandeja ordenada por fecha de llegada, sin eso, esconde la que ya incumple.
 *
 * ## Rechazar es resolver, y no es un fallo
 *
 * «Negarse a borrar los datos de un alumno con recibos pendientes es legítimo, y lo
 * que hace falta es que **quede escrito por qué**.» Por eso `rejected` no se pinta en
 * rojo, y por eso **la pantalla exige el motivo al rechazar** aunque el servidor lo
 * acepte vacío: una negativa sin motivo es exactamente lo que no se puede defender
 * delante de la Agencia dentro de dos años.
 *
 * ## Kiap no resuelve la solicitud: la registra
 *
 * Marcarla como resuelta no manda los datos ni los borra. Eso lo hace una persona, y
 * la pantalla lo dice: si no, alguien pulsa el botón y se queda tranquilo creyendo
 * que el sistema hizo el trabajo.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { fecha, fechaYHora } from '@/shared/ui/formato';
import {
  CLASES_DE_SOLICITUD,
  ESTADOS_DE_SOLICITUD,
  estaAbierta,
  etiquetaDeClaseDeSolicitud,
  etiquetaDeEstadoDeSolicitud,
  plazoEnPalabras,
  urgenciaDeSolicitud,
  type SolicitudDeDatos,
} from '@/club/cumplimiento/tipos';

export function SolicitudesDeDatos({
  club,
  puedeResolver,
  alAbrirAlumno,
}: {
  club: string;
  puedeResolver: boolean;
  alAbrirAlumno: (alumnoId: string) => void;
}) {
  const cache = useQueryClient();
  const [conResueltas, setConResueltas] = useState(false);
  const [resolviendo, setResolviendo] = useState<SolicitudDeDatos | null>(null);
  const [comoAcaba, setComoAcaba] = useState<'completed' | 'rejected'>(
    'completed'
  );
  const [motivo, setMotivo] = useState('');
  const [problema, setProblema] = useState<Problema | null>(null);

  const solicitudes = useQuery({
    queryKey: ['solicitudes-rgpd', club, conResueltas],
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        ['data-requests'].get({
          query: conResueltas ? { include_completed: true } : {},
        });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as SolicitudDeDatos[];
    },
  });

  const resolver = useMutation({
    mutationFn: async (s: SolicitudDeDatos) => {
      const r = await api.v1
        .clubs({ club })
        ['data-requests']({ requestId: s.id })
        .complete.post({
          outcome: comoAcaba,
          notes: motivo.trim() === '' ? null : motivo.trim(),
        });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setResolviendo(null);
      setMotivo('');
      setComoAcaba('completed');
      void cache.invalidateQueries({ queryKey: ['solicitudes-rgpd', club] });
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  // ── Resolver una ─────────────────────────────────────────────────────────
  if (resolviendo) {
    const clase = CLASES_DE_SOLICITUD[resolviendo.kind];
    /*
     * **El motivo es obligatorio al rechazar**, aunque el servidor lo acepte vacío:
     * una negativa sin motivo es lo que no se puede defender dentro de dos años.
     */
    const faltaMotivo = comoAcaba === 'rejected' && motivo.trim() === '';

    return (
      <form
        className="formulario"
        onSubmit={(e) => {
          e.preventDefault();
          setProblema(null);
          resolver.mutate(resolviendo);
        }}
      >
        <h1>Resolver la solicitud</h1>

        <dl className="datos">
          <div>
            <dt>Qué pide</dt>
            <dd>{etiquetaDeClaseDeSolicitud(resolviendo.kind)}</dd>
          </div>
          <div>
            <dt>Quién</dt>
            <dd>
              {resolviendo.user_name ?? 'Sin identificar'}
              {resolviendo.student_name
                ? ` · sobre ${resolviendo.student_name}`
                : ''}
            </dd>
          </div>
          <div>
            <dt>Cuándo llegó</dt>
            <dd>{fechaYHora(resolviendo.requested_at)}</dd>
          </div>
          <div>
            <dt>Plazo</dt>
            <dd>{plazoEnPalabras(resolviendo.days_to_due)}</dd>
          </div>
        </dl>

        {clase && <p className="tenue">{clase.explicacion}</p>}

        <h2 className="seccion">Cómo acaba</h2>

        <ul className="roles">
          <li>
            <label className="opcion">
              <input
                type="radio"
                name="acaba"
                checked={comoAcaba === 'completed'}
                onChange={() => setComoAcaba('completed')}
              />
              <span>
                <strong>Resuelta</strong>
                <em>Se ha hecho lo que pedía.</em>
              </span>
            </label>
          </li>
          <li>
            <label className="opcion">
              <input
                type="radio"
                name="acaba"
                checked={comoAcaba === 'rejected'}
                onChange={() => setComoAcaba('rejected')}
              />
              <span>
                <strong>Rechazada</strong>
                <em>
                  También es una resolución válida: negarse a borrar los datos
                  de un alumno con recibos pendientes es legítimo.
                </em>
              </span>
            </label>
          </li>
        </ul>

        <label>
          {comoAcaba === 'rejected' ? 'Por qué se rechaza' : 'Qué se ha hecho'}
          <textarea
            rows={3}
            maxLength={2000}
            required={comoAcaba === 'rejected'}
            placeholder={
              comoAcaba === 'rejected'
                ? 'Tiene tres recibos pendientes y la ley obliga a conservar la facturación seis años'
                : 'Se le ha mandado la exportación completa por correo el día 14'
            }
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
          />
        </label>

        {comoAcaba === 'rejected' ? (
          <div className="aviso" role="status">
            <strong>El motivo aquí no es papeleo.</strong> Si esta familia
            reclama a la Agencia dentro de dos años, esto es lo único que
            explica por qué el club dijo que no.
          </div>
        ) : (
          <p className="tenue">
            {/* Que nadie crea que el botón hizo el trabajo. */}
            <strong>Kiap no hace la gestión, la registra.</strong> Marcarlo como
            resuelto no manda los datos ni los borra: eso lo haces tú, y esto
            queda como prueba de cuándo y qué.
          </p>
        )}

        {problema && <PanelError problema={problema} />}

        <div className="acciones">
          <button type="submit" disabled={resolver.isPending || faltaMotivo}>
            {resolver.isPending
              ? 'Guardando…'
              : faltaMotivo
                ? 'Escribe por qué se rechaza'
                : comoAcaba === 'rejected'
                  ? 'Rechazarla'
                  : 'Marcarla como resuelta'}
          </button>
          <button
            type="button"
            className="btn-plano"
            disabled={resolver.isPending}
            onClick={() => {
              setResolviendo(null);
              setProblema(null);
            }}
          >
            Cancelar
          </button>
        </div>
      </form>
    );
  }

  // ── La bandeja ───────────────────────────────────────────────────────────
  if (solicitudes.isPending) {
    return <Cargando filas={4} etiqueta="Cargando las solicitudes" />;
  }

  if (solicitudes.isError || !solicitudes.data) {
    return (
      <PanelError
        problema={aProblema(solicitudes.error)}
        reintentar={() => void solicitudes.refetch()}
      />
    );
  }

  const lista = solicitudes.data;
  const abiertas = lista.filter(estaAbierta);
  const vencidas = abiertas.filter((s) => urgenciaDeSolicitud(s) === 'vencida');

  return (
    <section>
      <header className="titulo-fila">
        <h1>Solicitudes de datos</h1>
        <button
          type="button"
          className="btn-plano"
          onClick={() => setConResueltas((x) => !x)}
        >
          {conResueltas ? 'Solo las pendientes' : 'Ver también las resueltas'}
        </button>
      </header>

      <p className="tenue">
        Lo que piden las familias sobre sus datos: copia, corrección o borrado.{' '}
        <strong>El RGPD da un mes para responder</strong>, y pasarse no es una
        métrica interna.
      </p>

      {/* Lo vencido, primero y en rojo. Ver la cabecera. */}
      {vencidas.length > 0 && (
        <div className="aviso aviso-grave" role="alert">
          <strong>
            {vencidas.length === 1
              ? 'Hay una solicitud fuera de plazo.'
              : `Hay ${vencidas.length} solicitudes fuera de plazo.`}
          </strong>{' '}
          El plazo legal es de un mes desde que llega. Responder tarde, aunque
          sea para decir que no, es mejor que no responder.
        </div>
      )}

      {problema && <PanelError problema={problema} />}

      {lista.length === 0 ? (
        <Vacio
          titulo="No hay solicitudes"
          explicacion="Cuando una familia pida copia de sus datos, que se corrija algo o que se borre, aparecerá aquí con su plazo."
        />
      ) : (
        <ul className="solicitudes">
          {lista.map((s) => {
            const urgencia = urgenciaDeSolicitud(s);
            const pinta = ESTADOS_DE_SOLICITUD[s.status];
            const clase = CLASES_DE_SOLICITUD[s.kind];

            return (
              <li
                key={s.id}
                className={
                  urgencia === 'vencida'
                    ? 'fila-fuera'
                    : estaAbierta(s)
                      ? undefined
                      : 'fila-fuera'
                }
              >
                <div className="aviso-cabeza">
                  <strong>{etiquetaDeClaseDeSolicitud(s.kind)}</strong>
                  <span className={`pastilla ${pinta?.clase ?? 'pastilla'}`}>
                    {etiquetaDeEstadoDeSolicitud(s.status)}
                  </span>
                  {urgencia === 'vencida' && (
                    <span className="pastilla pastilla-mal">
                      Fuera de plazo
                    </span>
                  )}
                  {urgencia === 'pronto' && (
                    <span className="pastilla pastilla-paused">
                      Se acaba el plazo
                    </span>
                  )}
                </div>

                {clase && <p className="tenue">{clase.explicacion}</p>}

                <p className="tenue">
                  {s.user_name ?? 'Sin identificar'}
                  {s.student_id && s.student_name && (
                    <>
                      {' · sobre '}
                      <button
                        type="button"
                        className="enlace-fila"
                        onClick={() => alAbrirAlumno(s.student_id!)}
                      >
                        {s.student_name}
                      </button>
                    </>
                  )}
                </p>

                {/* Lo que se escribió al resolverla: es la defensa del club. */}
                {s.notes && <p>«{s.notes}»</p>}

                <div className="aviso-pie">
                  <span className="tenue">
                    Llegó {fecha(s.requested_at)}
                    {estaAbierta(s)
                      ? ` · ${plazoEnPalabras(s.days_to_due).toLowerCase()}`
                      : s.completed_at
                        ? ` · resuelta el ${fecha(s.completed_at)}`
                        : ''}
                  </span>

                  {puedeResolver && estaAbierta(s) && (
                    <button
                      type="button"
                      className="btn-plano"
                      onClick={() => {
                        setResolviendo(s);
                        setMotivo('');
                        setComoAcaba('completed');
                        setProblema(null);
                      }}
                    >
                      Resolverla
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
